import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getNavigatorShareInterface, isShareCancellation } from '#src/app/shared/share/utils';
import { copyToClipboard } from '#src/app/shared/utils/clipboard';
import { getFile } from '#src/app/shared/utils/file';

import { trackShare, trackShareFailure } from './track';

type OpenStoriesEducationModalResult = {
	waitForResult: () => Promise<{ successfulShare: boolean } | undefined>;
};

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	openStoriesEducationModal: (options: {
		onContinueAction: () => Promise<void>;
		shareVisualPreview: File | string | null;
	}) => OpenStoriesEducationModalResult;
};

export async function shareInstagramStories(
	data: { url: string; image: File | string | null },
	{ trackingEvents, trackingData, openStoriesEducationModal }: Options,
	track: TrackingFn,
	utilityContext: UtilityContext,
): Promise<boolean> {
	const { url } = data;

	const image = await toFile(data.image, utilityContext);

	const navigator = getNavigatorShareInterface();

	if (!image || !navigator) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const sharePayload = { files: [image] };

	const triggerNativeShare = async () => {
		try {
			/**
			 * Doing a trigger and forget copy to clipboard to avoid being
			 * hit by a transient user gesture error. `trackShare` below must stay synchronous
			 * for the same reason — no `await` may be introduced above `navigator.share()`, or the
			 * transient user gesture it depends on is consumed before the share sheet can open.
			 */
			void copyToClipboard(url, utilityContext.i18n);

			// Fired here, not on resolve: the link is already in the supporter's hands via the clipboard
			// write above, which makes this Continue tap — not a confirmed share — the causal event for a
			// recruit. `navigator.share()` can reject, or never settle at all (~14.5% of taps), and either
			// way the link has already been handed over. Fired exactly once per tap — do not add a second
			// emission on resolve.
			trackShare(trackingEvents.success, trackingData, track);

			await navigator.share(sharePayload);
		} catch (e) {
			const error = e as Error;

			// expected user cancellation, not a genuine failure — see isShareCancellation()
			if (isShareCancellation(error)) {
				return;
			}

			void utilityContext.errorReporter.report(new Error('Could not share to instagramStories', { cause: error }));

			trackShareFailure(
				trackingEvents.failure,
				{
					...trackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	trackShare(trackingEvents.intent, trackingData, track);

	const successfulShare = await (async () => {
		try {
			if (!navigator.canShare(sharePayload)) {
				return false;
			}
			const modalResult = await openStoriesEducationModal({
				onContinueAction: triggerNativeShare,
				shareVisualPreview: image,
			}).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	trackShare(successfulShare ? trackingEvents.continue : trackingEvents.cancel, trackingData, track);

	return successfulShare;
}

async function toFile(file: File | string | null, utilityContext: UtilityContext): Promise<File | null> {
	if (file instanceof File) {
		return file;
	}
	if (typeof file === 'string') {
		/**
		 * An issue was reported that happens for Android where, if we do not include the file `type` when
		 * triggering the native share, the flow breaks and throws an error.
		 * Adding the `type` by default to avoid this issue across the board.
		 *
		 * @see https://change.atlassian.net/browse/CHANGE-92124
		 */
		const res = await getFile(file, undefined, true);

		if (!res) {
			void utilityContext.errorReporter.report(
				new Error(`Failed to get image file from URL for instagramStories share: ${file}`),
			);
		}

		return res;
	}
	void utilityContext.errorReporter.report(new Error('Missing image for instagramStories share'));
	return null;
}
